import React, { useCallback, useEffect, useState } from 'react';
import apiClient, { usersAPI } from '../../api/client';
import { UserStatusBadge, getUserStatus } from '../ChangeStatusModal';
import {
  X, KeyRound, UserPlus, Copy, Check, AlertTriangle,
  RefreshCw, ToggleLeft, ToggleRight, Mail, Info,
} from 'lucide-react';
import { toast } from 'sonner';

// #ClientPanelAccess — el acceso del cliente es UNA sola cuenta (rol
// `cliente`) que sirve para el panel web y para la app móvil. La nota es
// fija y no configurable para que soporte no prometa credenciales
// distintas por plataforma.
const ACCESS_NOTE =
  'Con este acceso el cliente entra al panel web (admin.soempg.com) y a la app móvil con el mismo correo y contraseña, y solo ve sus proyectos, eventos y evaluaciones.';

const inputCls =
  'w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary';

const EMPTY_FORM = { first_name: '', last_name: '', email: '', send_credentials: true };

// #ClientPanelAccess — traduce los errores del backend a algo accionable.
// El 409 (correo ya registrado) es el más frecuente y el detail viene en
// inglés; sin esto el admin veía "Email already registered" y no sabía
// que la salida es restablecer la contraseña del acceso existente.
function accessErrorMessage(err, fallback) {
  if (err?.response?.status === 409) {
    return 'Ese correo ya tiene una cuenta en SOEM. Usa otro correo o restablece la contraseña del acceso que ya existe.';
  }
  if (!err?.response) {
    return 'No se pudo conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.';
  }
  const detail = err.response.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  return fallback;
}

function formatLastLogin(value) {
  if (!value) return 'Nunca';
  const d = new Date(value);
  if (isNaN(d.getTime())) return 'Nunca';
  return d.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
}

export default function ClientAccessModal({ client, onClose }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirmResetId, setConfirmResetId] = useState(null);
  const [resetSendEmail, setResetSendEmail] = useState(true);
  // #ClientPanelAccess — la contraseña temporal viaja UNA sola vez en la
  // respuesta del backend; se guarda en estado local (nunca en storage ni
  // en la lista) para poder copiarla y se descarta al cerrar el aviso.
  const [tempCredential, setTempCredential] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await usersAPI.list({ client_id: client.id, role: 'cliente', limit: 200 });
      setUsers((r.data?.items || []).filter((u) => !u.is_archived));
    } catch (err) {
      toast.error(accessErrorMessage(err, 'Error al cargar los accesos del cliente'));
    } finally {
      setLoading(false);
    }
  }, [client.id]);

  useEffect(() => { load(); }, [load]);

  const copyPassword = async () => {
    if (!tempCredential?.password) return;
    try {
      await navigator.clipboard.writeText(tempCredential.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Contraseña copiada');
    } catch {
      // #ClientPanelAccess — el portapapeles falla en contextos no seguros
      // (http) o si el usuario lo bloqueó; la contraseña sigue visible en
      // pantalla, así que solo hay que decirle que la copie a mano.
      toast.error('Tu navegador bloqueó el portapapeles. Copia la contraseña manualmente.');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const first = form.first_name.trim();
    const last = form.last_name.trim();
    const email = form.email.trim().toLowerCase();
    if (!first) { toast.error('Escribe el nombre del contacto'); return; }
    if (!last) { toast.error('Escribe los apellidos del contacto'); return; }
    if (!email) { toast.error('Escribe el correo del contacto'); return; }
    setSaving(true);
    try {
      const r = await usersAPI.create({
        first_name: first,
        last_name: last,
        name: `${first} ${last}`,
        email,
        role: 'cliente',
        client_id: client.id,
        send_credentials: form.send_credentials,
      });
      const data = r.data || {};
      const created = data.user || data;
      const password = data.temp_password || created?.temp_password || null;
      toast.success('Acceso creado');
      if (password) {
        setTempCredential({
          title: 'Acceso creado — contraseña temporal',
          email,
          password,
          emailSent: typeof data.email_sent === 'boolean' ? data.email_sent : form.send_credentials,
        });
        setCopied(false);
      }
      setForm(EMPTY_FORM);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(accessErrorMessage(err, 'No se pudo crear el acceso'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (user) => {
    setBusyId(user.id);
    try {
      const r = await apiClient.post(`/users/${user.id}/reset-password`, { send_email: resetSendEmail });
      const data = r.data || {};
      setConfirmResetId(null);
      toast.success('Contraseña restablecida');
      if (data.temp_password) {
        setTempCredential({
          title: 'Contraseña restablecida — contraseña temporal',
          email: user.email,
          password: data.temp_password,
          emailSent: !!data.email_sent,
        });
        setCopied(false);
      }
      load();
    } catch (err) {
      toast.error(accessErrorMessage(err, 'No se pudo restablecer la contraseña'));
    } finally {
      setBusyId(null);
    }
  };

  const toggleStatus = async (user) => {
    const isActive = getUserStatus(user) === 'activo';
    const next = isActive ? 'inactivo' : 'activo';
    const label = user.full_name || user.name || user.email;
    if (isActive && !window.confirm(
      `¿Desactivar el acceso de ${label}? Dejará de poder entrar al panel web y a la app móvil.`
    )) return;
    setBusyId(user.id);
    try {
      await usersAPI.changeStatus(user.id, { status: next });
      toast.success(next === 'activo' ? 'Acceso activado' : 'Acceso desactivado');
      load();
    } catch (err) {
      toast.error(accessErrorMessage(err, 'No se pudo cambiar el estado del acceso'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="client-access-modal">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-2xl animate-fade-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-soem-navy sticky top-0 z-10">
          <div className="flex items-center gap-2.5">
            <KeyRound size={18} className="text-white flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="font-bold text-white text-sm">Accesos del panel</h3>
              <p className="text-slate-300 text-xs truncate">{client.name}</p>
            </div>
          </div>
          <button type="button" aria-label="Cerrar" onClick={onClose}>
            <X size={18} className="text-slate-400 hover:text-white" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Nota fija: qué implica este acceso */}
          <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-sm p-3">
            <Info size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-800">{ACCESS_NOTE}</p>
          </div>

          {/* Contraseña temporal — se muestra una sola vez */}
          {tempCredential && (
            <div className="border-2 border-amber-300 bg-amber-50 rounded-sm p-4 space-y-3" data-testid="temp-password-block">
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-bold text-amber-900">{tempCredential.title}</p>
                  <p className="text-xs text-amber-800 mt-0.5 font-semibold">
                    Guárdala ahora: no volverá a mostrarse.
                  </p>
                </div>
              </div>
              <p className="text-xs text-amber-900">
                <span className="font-semibold">Correo:</span> {tempCredential.email}
              </p>
              <div className="flex items-center gap-2 bg-white border border-amber-200 rounded-sm px-3 py-2">
                <code className="text-sm font-mono text-soem-navy flex-1 break-all">{tempCredential.password}</code>
                <button type="button" onClick={copyPassword} title="Copiar contraseña"
                  aria-label="Copiar contraseña" className="text-soem-primary flex-shrink-0"
                  data-testid="copy-temp-password">
                  {copied ? <Check size={15} className="text-green-600" /> : <Copy size={15} />}
                </button>
              </div>
              <p className="text-xs text-amber-800 flex items-center gap-1.5">
                <Mail size={12} className="flex-shrink-0" />
                {tempCredential.emailSent
                  ? 'También se envió por correo al cliente.'
                  : 'No se envió por correo: compártela tú por un medio seguro.'}
              </p>
              <button type="button" onClick={() => { setTempCredential(null); setCopied(false); }}
                className="soem-btn-secondary px-3 py-1.5 text-xs">
                Ya la guardé
              </button>
            </div>
          )}

          {/* Encabezado de lista + alta */}
          <div className="flex items-center justify-between gap-3">
            <p className="soem-label">Usuarios con acceso ({users.length})</p>
            {!showForm && (
              <button type="button" onClick={() => setShowForm(true)}
                className="soem-btn-primary px-3 py-1.5 text-xs flex items-center gap-1.5"
                data-testid="create-access-btn">
                <UserPlus size={13} /> Crear acceso
              </button>
            )}
          </div>

          {showForm && (
            <form onSubmit={handleCreate} className="border border-slate-200 bg-slate-50 rounded-sm p-4 space-y-3"
              data-testid="create-access-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="f-acceso-nombres" className="soem-label block mb-1">Nombre(s) *</label>
                  <input id="f-acceso-nombres" className={inputCls} value={form.first_name} required
                    placeholder="Ej: María"
                    onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} />
                </div>
                <div>
                  <label htmlFor="f-acceso-apellidos" className="soem-label block mb-1">Apellido(s) *</label>
                  <input id="f-acceso-apellidos" className={inputCls} value={form.last_name} required
                    placeholder="Ej: Hernández Ruiz"
                    onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} />
                </div>
              </div>
              <div>
                <label htmlFor="f-acceso-email" className="soem-label block mb-1">Correo *</label>
                <input id="f-acceso-email" type="email" className={inputCls} value={form.email} required
                  placeholder="contacto@empresa.com"
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.send_credentials}
                  onChange={(e) => setForm((f) => ({ ...f, send_credentials: e.target.checked }))} />
                <span className="text-sm text-slate-600">Enviar contraseña por correo</span>
              </label>
              <div className="flex gap-2 pt-1">
                <button type="submit" disabled={saving}
                  className="soem-btn-primary px-4 py-2 text-sm disabled:opacity-60"
                  data-testid="submit-access-btn">
                  {saving ? 'Creando...' : 'Crear acceso'}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }}
                  className="soem-btn-secondary px-4 py-2 text-sm">
                  Cancelar
                </button>
              </div>
            </form>
          )}

          {loading ? (
            <div className="space-y-2">
              {[...Array(2)].map((_, i) => <div key={i} className="h-16 bg-slate-100 rounded-sm animate-pulse" />)}
            </div>
          ) : users.length === 0 ? (
            <p className="text-slate-400 text-sm text-center py-8">
              Este cliente todavía no tiene accesos al panel. Crea el primero para que pueda entrar.
            </p>
          ) : (
            <div className="border border-slate-200 rounded-sm divide-y divide-slate-100">
              {users.map((u) => {
                const isActive = getUserStatus(u) === 'activo';
                const busy = busyId === u.id;
                return (
                  <div key={u.id} className="p-3" data-testid={`client-access-row-${u.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <p className="font-semibold text-soem-navy text-sm truncate">
                          {u.full_name || u.name || '—'}
                        </p>
                        <p className="text-xs text-slate-400 truncate">{u.email}</p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          Último acceso: {formatLastLogin(u.last_login_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <UserStatusBadge user={u} />
                        <button type="button" disabled={busy}
                          onClick={() => { setConfirmResetId(u.id); setResetSendEmail(true); }}
                          className="text-slate-400 hover:text-orange-600 disabled:opacity-50"
                          title="Restablecer contraseña" aria-label={`Restablecer contraseña de ${u.email}`}
                          data-testid={`reset-access-password-${u.id}`}>
                          <RefreshCw size={15} />
                        </button>
                        <button type="button" disabled={busy} onClick={() => toggleStatus(u)}
                          className={`disabled:opacity-50 ${isActive ? 'text-green-600 hover:text-green-700' : 'text-slate-400 hover:text-soem-primary'}`}
                          title={isActive ? 'Desactivar acceso' : 'Activar acceso'}
                          aria-label={isActive ? `Desactivar acceso de ${u.email}` : `Activar acceso de ${u.email}`}
                          data-testid={`toggle-access-${u.id}`}>
                          {isActive ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                        </button>
                      </div>
                    </div>

                    {confirmResetId === u.id && (
                      <div className="mt-3 border border-orange-200 bg-orange-50 rounded-sm p-3 space-y-2"
                        data-testid={`confirm-reset-${u.id}`}>
                        <p className="text-xs text-orange-900">
                          Se generará una contraseña nueva y la actual dejará de funcionar de inmediato. ¿Continuar?
                        </p>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={resetSendEmail}
                            onChange={(e) => setResetSendEmail(e.target.checked)} />
                          <span className="text-xs text-orange-900">Enviar la contraseña nueva por correo</span>
                        </label>
                        <div className="flex gap-2">
                          <button type="button" disabled={busy} onClick={() => handleReset(u)}
                            className="soem-btn-primary px-3 py-1.5 text-xs disabled:opacity-60"
                            data-testid={`confirm-reset-btn-${u.id}`}>
                            {busy ? 'Restableciendo...' : 'Sí, restablecer'}
                          </button>
                          <button type="button" onClick={() => setConfirmResetId(null)}
                            className="soem-btn-secondary px-3 py-1.5 text-xs">
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="pt-1">
            <button type="button" onClick={onClose} className="soem-btn-secondary w-full py-2 text-sm">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
